import {
  ContributionClassesAndRolesDDL,
  ContributionsListItem,
} from 'types/contribution';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ChakraEmptyList,
  ChakraFabButton,
  ContentContainer,
} from 'app/components/Core';
import { ContentContainerType } from 'types/enums';
import { QrCheckin } from 'app/components/Song/Contributions/QrCheckin';
import { ReactComponent as AddIcon } from 'resources/images/add.svg';
import { ReactComponent as CreditsIcon } from 'resources/images/credits.svg';
import { useContextMenu } from 'app/components/ChakraTableList';
import { getColumns } from 'app/components/Song/Contributions/ContributionsList/utils';
import { DeleteSongContributorDialog } from 'app/components/Song/Contributions/DeleteSongContributorDialog';
import { ContributionsListDialog } from 'app/components/Song/Contributions/ContributionsListDialog';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { OverlayDialog } from 'app/components/Dialogs';
import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { useContributionsListState } from './hooks';
import { ContributionsListComponentProps } from './types';
import { ContributionRow } from './ContributionRow';
import { ContributionFormOverlay } from '../ContributionFormOverlay';

export const ContributionsListComponent: React.FC<ContributionsListComponentProps> = ({
  loading,
  contributions,
  dispatch,
  canAddContribution,
  contributionClassesAndRoles = {} as ContributionClassesAndRolesDDL,
  getAffectSplit,
}) => {
  const showEmptyState = !loading && contributions?.length === 0;

  const {
    handleContributorDelete,
    handleDeleteContribution,
    dialogOpen,
    setDialogOpen,
  } = useContributionsListState(dispatch);

  const tableKey = 'credits';
  const ref = useRef<HTMLDivElement>(null);
  const contextMenuProps = useContextMenu();
  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns(totalWidth), [totalWidth]);

  const [contributionDialog, setContributionDialog] = useState<{
    open: boolean;
    contributionGuid?: string;
  }>({ open: false });

  const handleOpenEditDialog = useCallback(
    (contributionGuid?: string) => {
      setContributionDialog({ open: true, contributionGuid });
      setDialogOpen(undefined);
    },
    [setDialogOpen]
  );

  const rowRenderer = useCallback(
    (row: Row<ContributionsListItem>) => {
      const options = [
        {
          title: 'Remove',
          value: 'DELETE',
          onClick: () => {
            setDialogOpen({
              type: 'delete',
              contributionClassName: (row.getParentRow()?.original as any)
                .className,
              contributor: row.original,
            });
          },
        },
      ];

      return (
        <ContributionRow
          key={row.id}
          contributionClassesAndRoles={contributionClassesAndRoles}
          row={row}
          handleOpenEditDialog={handleOpenEditDialog}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
          setDialogOpen={setDialogOpen}
        />
      );
    },
    [
      contextMenuProps,
      contributionClassesAndRoles,
      handleOpenEditDialog,
      setDialogOpen,
    ]
  );

  const handleClose = () => {
    setContributionDialog({ open: false });
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  return (
    <>
      <ContentContainer
        type={ContentContainerType.LIST}
        isViewContent
        pb={30}
        centerContent={showEmptyState}
      >
        {anchorEl && (
          <QrCheckin anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
        )}
        <Menu preventOverflow={false} gutter={4} placement="top" flip={false}>
          <ChakraFabButton
            as={MenuButton}
            isDisabled={!canAddContribution}
            sx={{
              '> span': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
            aria-label="add credit"
            leftIcon={<AddIcon />}
          >
            Add Credit
          </ChakraFabButton>
          <MenuList
            padding={0}
            minWidth="112px"
            sx={{ WebkitAppRegion: 'no-drag' }}
          >
            <MenuItem
              data-testid="scan-qr"
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
              }}
            >
              Scan QR
            </MenuItem>
            <MenuItem
              data-testid="add-credit-manually"
              onClick={() => setContributionDialog({ open: true })}
            >
              Add manually
            </MenuItem>
          </MenuList>
        </Menu>
        {showEmptyState ? (
          <ChakraEmptyList
            icon={<CreditsIcon role="img" />}
            title="Adding a Credit"
            primaryText="Ensure that everyone involved in making the record gets the credit they deserve by scanning the QR code."
          />
        ) : (
          <>
            <StickyTable<any>
              tableKey={tableKey}
              ref={ref}
              data={contributions}
              columns={columns}
              rowRenderer={rowRenderer}
              loading={loading}
              contextMenuProps={contextMenuProps}
              getSubRows={(row: any) => {
                return Object.values(row?.items || {});
              }}
            />
          </>
        )}
      </ContentContainer>
      {dialogOpen?.type === 'delete' && (
        <DeleteSongContributorDialog
          open
          onClose={() => setDialogOpen(undefined)}
          onConfirm={handleContributorDelete}
          affectsSplit={getAffectSplit(dialogOpen.contributionClassName)}
          contributionsClassName={dialogOpen.contributionClassName}
          contributor={dialogOpen.contributor}
        />
      )}
      {dialogOpen?.type === 'contributionsList' && (
        <ContributionsListDialog
          isOpen
          onClose={() => setDialogOpen(undefined)}
          contributionClassesAndRoles={contributionClassesAndRoles}
          deleteContributionAction={handleDeleteContribution}
          contributionItem={dialogOpen.contributor}
          contributionClassName={dialogOpen.contributionClassName}
          handleOpenEditDialog={handleOpenEditDialog}
        />
      )}
      {contributionDialog.open && (
        <OverlayDialog onClose={handleClose}>
          <ContributionFormOverlay
            onClose={() => setContributionDialog({ open: false })}
            contributionGuid={contributionDialog.contributionGuid}
          />
        </OverlayDialog>
      )}
    </>
  );
};
